import React, { useState, useEffect, ChangeEvent } from 'react'
import MaskedInput from 'react-maskedinput'
import theme from 'live/services/theme'
import DatePicker, { ReactDatePickerProps } from 'react-datepicker'
import CustomDateInput from 'live/components/forms/ui/custom-react-datepicker-input'
import DateService from 'live/services/date'
import styled from 'styled-components'
import moment from 'moment'

export type Props = {
    title?: string
    timestamp: number
    setTimestamp: (timestamp: number) => void
    setEnable?: (enable?: boolean) => void
    error?: boolean
    setError?: (error?: boolean) => void
    required?: boolean
    datePickerProps?: Record<string,unknown>
}

const DateTimePicker = (props: Props): React.ReactElement => {
    const { title, timestamp, setTimestamp, required, error, setError, setEnable } = props

    const [date, setDate] = useState(timestamp ? moment(moment(timestamp).format('YYYYMMDD')) : null)
    const [time, setTime] = useState(timestamp ? moment(timestamp).format('HH:mm:ss') : '')

    useEffect(() => {
        if (date?.format) {
            setTimestamp(getUnixFromDateAndTime(date, time))
        }
    }, [date, time, setTimestamp])

    const getUnixFromDateAndTime = (mom: moment.Moment, time?: string): number => {
        time = time || '00:00:00'
        time = time.replace(/_/g, '0')
        const m = moment(`${mom.format('YYYYMMDD')}${time}`, 'YYYYMMDDHH:mm:ss', true)

        return m.valueOf()
    }

    return (
        <ContainerDate>
            <Label>
                {title}
                {required ? <Required>*</Required> : null}
            </Label>
            <WrapperFlex validation={error}>
                <div className="mr-5">
                    <DatePicker
                        customInput={<CustomDateInput invalid={error} />}
                        autoComplete="false"
                        selected={date}
                        onChange={value => {
                            if (value === null) {
                                if (setError) setError(true)
                                if(setEnable) setEnable(false)
                                setDate(null)
                                setTime(null)
                            } else {
                                if (setError) setError(false)
                                if(setEnable) setEnable(true)
                                setDate(value)
                            }
                        }}
                        dateFormat={DateService.getDateFormat()}
                        placeholderText={DateService.getDateFormat().toLowerCase()}
                        require
                        peekNextMonth
                        showYearDropdown
                        showMonthDropdown
                        {...(props.datePickerProps || {})}
                    />
                </div>
                <ContainerMasked>
                    <MaskedInput
                        className={props.error ? 'error' : ''}
                        type="text"
                        value={time}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setTime(e.target.value)
                        }}
                        mask="11:11:11"
                        placeholder={'hh:mm:ss'}
                        disabled={!date}
                    />
                </ContainerMasked>
            </WrapperFlex>
        </ContainerDate>
    )
}

export default DateTimePicker

const ContainerDate = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

const WrapperFlex = styled.div<{ validation?: boolean }>`
    display: flex;
    width: 100%;
    .input-group {
        width: 120px;
    }
    .react-datepicker__input-container input {
        border: ${({ validation }) => (validation ? `1px solid ${theme['brand-danger']}` : '')};
    }
`

const ContainerMasked = styled.div`
    input {
        width: 66px;
        font-size: 12px;
    }
`

const Label = styled.span`
    display: flex;
    font-family: ${theme['font-semibold']};
    font-size: 13px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
`

const Required = styled.span`
    color: ${theme['dark-red']};
    width: 27px;
    height: 18px;
    font-family: ${theme['font-semibold']};
    font-size: 13px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
`
